import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderPlus, FileText, Image as ImageIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ImageUpload } from './ImageUpload';
import { LIMITS, getCharCountColor, getCharCountMessage, getCharCountStatus } from '../lib/constants';

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, description: string, imageUrl: string | null) => Promise<void>;
}

export function CreateProjectModal({
  open,
  onClose,
  onSave,
}: CreateProjectModalProps) {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setImageUrl(null);
    }
  }, [open]);

  const handleImageChange = (url: string | null) => {
    setImageUrl(url);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      return;
    }

    if (name.trim().length > LIMITS.PROJECT_NAME_MAX) {
      return;
    }

    if (description.length > LIMITS.PROJECT_DESCRIPTION_MAX) {
      return;
    }

    try {
      setSaving(true);
      await onSave(name.trim(), description.trim(), imageUrl);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setImageUrl(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-1.5">
          <DialogTitle className="text-lg flex items-center gap-2">
            <FolderPlus className="h-5 w-5 text-primary" />
            {t('project.new_project')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('project.create_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="project-name" className="text-sm">
              {t('project.project_name')} *
            </Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('project.project_name_placeholder')}
              maxLength={LIMITS.PROJECT_NAME_MAX}
              autoFocus
              disabled={saving}
              className={getCharCountStatus(name.length, LIMITS.PROJECT_NAME_MAX) === 'exceeded' ? 'border-red-500 focus-visible:ring-red-500' : ''}
            />
            <p className={`text-xs ${getCharCountColor(name.length, LIMITS.PROJECT_NAME_MAX)}`}>
              {getCharCountMessage(name.length, LIMITS.PROJECT_NAME_MAX)}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="project-description" className="text-sm">
              {t('project.description')} ({t('optional')})
            </Label>
            <Textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('project.description_placeholder')}
              rows={2}
              maxLength={LIMITS.PROJECT_DESCRIPTION_MAX}
              disabled={saving}
              className={`resize-none ${getCharCountStatus(description.length, LIMITS.PROJECT_DESCRIPTION_MAX) === 'exceeded' ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
            />
            <p className={`text-xs ${getCharCountColor(description.length, LIMITS.PROJECT_DESCRIPTION_MAX)}`}>
              {getCharCountMessage(description.length, LIMITS.PROJECT_DESCRIPTION_MAX)}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">
              {t('project.cover_image')} ({t('optional')})
            </Label>
            <ImageUpload
              currentImage={imageUrl}
              onImageChange={handleImageChange}
              disabled={saving}
            />
          </div>

          {}
          <div className="flex items-start gap-2 p-2.5 bg-primary/5 border border-primary/20 rounded-lg">
            <FileText className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-medium text-foreground">
                {t('tip')}
              </p>
              <p className="text-muted-foreground mt-0.5">
                {t('project.tip_message')}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim() || name.trim().length > LIMITS.PROJECT_NAME_MAX || description.length > LIMITS.PROJECT_DESCRIPTION_MAX}
            className="bg-primary hover:bg-primary/90"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                {t('project.creating')}
              </>
            ) : (
              t('dashboard.create_project')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
